import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Search, X } from "lucide-react";
import Swal from "sweetalert2";

import { fetchConsultantsRequest } from "../../../redux/actions/consultantsActions";
import { fetchBudgetByCampagneRequest } from "../../../redux/actions/campagnesActions";
import { getAllEvaluationsRequest } from "../../../redux/actions/evaluationActions";
import "../../Formation/ResponsableFormation/RessourcesStyles/EventManagement.css";
import { buildRichEventPayload, toDatetimeLocalValue, toFloat } from "../utils/calendarHelpers";

const EMPTY_FORM = {
    titre: "",
    description: "",
    moduleCleId: "",
    locationType: "ONSITE",
    salleId: "",
    offsiteLocation: "",
    dateDebut: "",
    dateFin: "",
    allowRegistration: true,
    registrationDeadlineHours: "",
    isPeriodic: false,
    maxPlaces: "",
    currency: "MAD",
};

const EMPTY_BUDGET = {
    tarifFormation: 0,
    repas: 0,
    transport: 0,
    hebergement: 0,
    salleConference: 0,
    autres: 0,
    autreDescription: "",
};

const normalize = (value) => String(value || "").trim().toLowerCase();
const isHotEvaluation = (evaluation) =>
    evaluation?.typeCode === "HOT" || String(evaluation?.evaluationType || "").toLowerCase().includes("chaud");

export default function EventFormModal({ open, mode, isEdit: isEditProp, event, modules, salles, onClose, onSubmit, loading }) {
    const dispatch = useDispatch();
    const consultants = useSelector((state) => state.consultants?.consultants || []);
    const evaluations = useSelector((state) => state.evaluations?.evaluations || []);
    const campaignBudget = useSelector((state) => state.campagnes?.budgets || null);
    const isEdit = mode ? mode === "edit" : Boolean(isEditProp);

    const [tab, setTab] = useState("INFOS");
    const [form, setForm] = useState(EMPTY_FORM);
    const [budget, setBudget] = useState(EMPTY_BUDGET);
    const [consultSearch, setConsultSearch] = useState("");
    const [evaluationSearch, setEvaluationSearch] = useState("");
    const [selectedConsultantIds, setSelectedConsultantIds] = useState(new Set());
    const [selectedEvaluationIds, setSelectedEvaluationIds] = useState(new Set());
    const [numberOfDaysHot, setNumberOfDaysHot] = useState(1);
    const [numberOfDaysCold, setNumberOfDaysCold] = useState(120);
    const [seancesCount, setSeancesCount] = useState(0);
    const [seances, setSeances] = useState([]);

    useEffect(() => {
        if (!open) return;
        dispatch(fetchConsultantsRequest());
        dispatch(getAllEvaluationsRequest());
    }, [dispatch, open]);

    useEffect(() => {
        if (!open) return;
        if (!event || !isEdit) {
            setTab("INFOS");
            setForm(EMPTY_FORM);
            setBudget(EMPTY_BUDGET);
            setConsultSearch("");
            setEvaluationSearch("");
            setSelectedConsultantIds(new Set());
            setSelectedEvaluationIds(new Set());
            setNumberOfDaysHot(1);
            setNumberOfDaysCold(120);
            setSeancesCount(0);
            setSeances([]);
            return;
        }

        setTab("INFOS");
        setForm({
            titre: event.titre || "",
            description: event.description || "",
            moduleCleId: event.moduleCle?.id ? String(event.moduleCle.id) : "",
            locationType: event.locationType || "ONSITE",
            salleId: event.salle?.id ? String(event.salle.id) : "",
            offsiteLocation: event.offsiteLocation || "",
            dateDebut: toDatetimeLocalValue(event.dateDebut),
            dateFin: toDatetimeLocalValue(event.dateFin),
            allowRegistration: Boolean(event.allowRegistration),
            registrationDeadlineHours: event.registrationDeadlineHours != null ? String(event.registrationDeadlineHours) : "",
            isPeriodic: Boolean(event.isPeriodic),
            maxPlaces: event.maxPlaces != null ? String(event.maxPlaces) : "",
            currency: event.currency || "MAD",
        });
        setBudget({
            tarifFormation: event.tarifFormation || 0,
            repas: event.repas || 0,
            transport: event.transport || 0,
            hebergement: event.hebergement || 0,
            salleConference: event.salleConference || 0,
            autres: event.autres || 0,
            autreDescription: event.autreDescription || "",
        });
        setSelectedConsultantIds(new Set((event.consultants || []).map((item) => item.id)));
        setSelectedEvaluationIds(new Set((event.evaluations || []).filter(item => item?.typeCode === "HOT" || item?.typeCode === "COLD").map((item) => item.id)));
        setNumberOfDaysHot(event.numberOfDaysHot != null ? event.numberOfDaysHot : 1);
        setNumberOfDaysCold(event.numberOfDaysCold != null ? event.numberOfDaysCold : 120);
        const existingSessions = (event.sessions || []).map((session, index) => ({
            id: session.id || null,
            sorder: session.sorder || index + 1,
            name: session.name || `Séance ${index + 1}`,
            dureeHours: session.dureeHours || 1,
            startat: toDatetimeLocalValue(session.startat),
            endat: toDatetimeLocalValue(session.endat),
        }));
        setSeancesCount(existingSessions.length);
        setSeances(existingSessions);
        if (event.moduleCle?.campagne?.id) dispatch(fetchBudgetByCampagneRequest(event.moduleCle.campagne.id));
    }, [dispatch, event, isEdit, open]);

    const sallesFiltrees = useMemo(() => {
        if (!salles) return [];
        if (form.locationType === "ONSITE") return salles.filter((salle) => salle.type === "PHYSIQUE");
        if (form.locationType === "VIRTUELLE") return salles.filter((salle) => salle.type === "VIRTUELLE");
        return [];
    }, [form.locationType, salles]);

    const filteredConsultants = useMemo(() => {
        const query = normalize(consultSearch);
        return consultants.filter((item) =>
            !query || normalize(item.nomContact).includes(query) || normalize(item.specialite).includes(query)
        );
    }, [consultSearch, consultants]);

    const filteredEvaluations = useMemo(() => {
        const query = normalize(evaluationSearch);
        return (evaluations || [])
            .filter((item) => item?.typeCode === "HOT" || item?.typeCode === "COLD")
            .filter((item) =>
                !query || normalize(item.name || item.nom).includes(query) || normalize(item.typeCode).includes(query)
            );
    }, [evaluationSearch, evaluations]);

    const hotEvaluations = useMemo(() => filteredEvaluations.filter(item => item.typeCode === "HOT"), [filteredEvaluations]);
    const coldEvaluations = useMemo(() => filteredEvaluations.filter(item => item.typeCode === "COLD"), [filteredEvaluations]);

    const formationModules = useMemo(() => {
        return (modules || []).filter(
            (m) =>
                m?.type === "FORMATION" ||
                m?.typeCode === "FORMATION" ||
                m?.typeModule === "FORMATION" ||
                m?.moduleType === "FORMATION"
        );
    }, [modules]);

    const totalCost = useMemo(
        () => toFloat(budget.tarifFormation) + toFloat(budget.repas) + toFloat(budget.transport) + toFloat(budget.hebergement) + toFloat(budget.salleConference) + toFloat(budget.autres),
        [budget]
    );

    const remainingBudget = useMemo(() => {
        if (!campaignBudget) return 0;
        const source = campaignBudget.budgetRestant != null ? campaignBudget.budgetRestant : campaignBudget.montant;
        return toFloat(source) - totalCost;
    }, [campaignBudget, totalCost]);

    if (!open) return null;

    const setField = (name, value) => setForm((current) => ({ ...current, [name]: value }));
    const toggleConsultant = (id) => setSelectedConsultantIds((current) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const toggleEvaluation = (id) => setSelectedEvaluationIds((current) => {
        const next = new Set(current);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });
    const handleSessionsCountChange = (value) => {
        const nextValue = Math.max(0, Math.min(30, Number(value) || 0));
        setSeancesCount(nextValue);
        setSeances((current) => Array.from({ length: nextValue }, (_, index) => ({
            id: current[index]?.id || null,
            sorder: current[index]?.sorder || index + 1,
            name: current[index]?.name || `Séance ${index + 1}`,
            dureeHours: current[index]?.dureeHours || 1,
            startat: current[index]?.startat || "",
            endat: current[index]?.endat || "",
        })));
    };
    const handleModuleChange = (moduleId) => {
        setField("moduleCleId", moduleId);
        const module = (modules || []).find((item) => String(item.id) === String(moduleId));
        if (module?.campagneId) dispatch(fetchBudgetByCampagneRequest(module.campagneId));
        if (module?.campagne?.id) dispatch(fetchBudgetByCampagneRequest(module.campagne.id));
    };
    const validate = () => {
        if (!form.titre.trim()) return "Titre obligatoire";
        if (!form.moduleCleId) return "Module obligatoire";
        if (!form.dateDebut || !form.dateFin) return "Dates obligatoires";
        if (form.locationType === "OFFSITE" && !form.offsiteLocation.trim()) return "Lieu externe obligatoire";
        if (form.locationType !== "OFFSITE" && !form.salleId) return "Salle obligatoire";

        const validEvalIds = new Set(
            (evaluations || [])
                .filter(ev => ev?.typeCode === "HOT" || ev?.typeCode === "COLD")
                .map(ev => ev.id)
        );
        for (const selectedId of selectedEvaluationIds) {
            if (!validEvalIds.has(selectedId)) {
                return "Seules les évaluations de type HOT ou COLD peuvent être associées à un événement.";
            }
        }
        return null;
    };
    const handleSubmit = () => {
        const error = validate();
        if (error) {
            Swal.fire({ icon: "warning", text: error, timer: 2000, showConfirmButton: false });
            return;
        }
        onSubmit(buildRichEventPayload({
            event: isEdit ? event : null,
            form,
            budget,
            consultants: Array.from(selectedConsultantIds),
            evaluations: Array.from(selectedEvaluationIds),
            sessions: seances,
            numberOfDaysHot,
            numberOfDaysCold,
            budgetRestant: remainingBudget,
        }));
    };

    return (
        <div className="ev-modal-overlay" onClick={onClose}>
            <div className="ev-modal ev-modal--large" onClick={(evt) => evt.stopPropagation()}>
                <div className="ev-modal-header">
                    <h3>{isEdit ? "Modifier l'événement" : "Ajouter un événement"}</h3>
                    <button type="button" className="ev-x" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </div>
                <div className="ev-modal-tabs">
                    {["INFOS", "SESSIONS", "CONSULTANTS", "EVALUATIONS", "BUDGET"].map((item) => (
                        <button key={item} type="button" className={`ev-modal-tab ${tab === item ? "active" : ""}`} onClick={() => setTab(item)}>
                            {item === "SESSIONS" ? `Séances (${seancesCount})` : item === "CONSULTANTS" ? `Consultants (${selectedConsultantIds.size})` : item === "EVALUATIONS" ? `Évaluations (${selectedEvaluationIds.size})` : item.charAt(0) + item.slice(1).toLowerCase()}
                        </button>
                    ))}
                </div>
                <div className="ev-modal-body">
                    {tab === "INFOS" && (
                        <div className="ev-grid">
                            <div className="ev-field"><label>Titre *</label><input className="ev-input" value={form.titre} onChange={(evt) => setField("titre", evt.target.value)} /></div>
                            <div className="ev-field"><label>Module *</label><select className="ev-input" value={form.moduleCleId} onChange={(evt) => handleModuleChange(evt.target.value)}><option value="">— Choisir —</option>{(formationModules || []).map((module) => <option key={module.id} value={String(module.id)}>{module.nom}</option>)}</select></div>
                            <div className="ev-field"><label>Lieu *</label><select className="ev-input" value={form.locationType} onChange={(evt) => setForm((current) => ({ ...current, locationType: evt.target.value, salleId: "", offsiteLocation: "" }))}><option value="ONSITE">On-site</option><option value="OFFSITE">Off-site</option><option value="VIRTUELLE">Virtuelle</option></select></div>
                            {form.locationType === "OFFSITE" ? <div className="ev-field ev-span-2"><label>Adresse exacte</label><input className="ev-input" value={form.offsiteLocation} onChange={(evt) => setField("offsiteLocation", evt.target.value)} /></div> : <>
                                <div className="ev-field"><label>{form.locationType === "VIRTUELLE" ? "Salle virtuelle" : "Salle"}</label><select className="ev-input" value={form.salleId} onChange={(evt) => setField("salleId", evt.target.value)}><option value="">— Choisir —</option>{sallesFiltrees.map((salle) => <option key={salle.id} value={String(salle.id)}>{salle.nom}</option>)}</select></div>
                                {form.locationType === "VIRTUELLE" && <div className="ev-field"><label>Lien visio</label><input className="ev-input" value={form.offsiteLocation} onChange={(evt) => setField("offsiteLocation", evt.target.value)} /></div>}
                            </>}
                            <div className="ev-field"><label>Début *</label><input type="datetime-local" className="ev-input" value={form.dateDebut} onChange={(evt) => setField("dateDebut", evt.target.value)} /></div>
                            <div className="ev-field"><label>Fin *</label><input type="datetime-local" className="ev-input" value={form.dateFin} onChange={(evt) => setField("dateFin", evt.target.value)} /></div>
                            <div className="ev-field ev-span-2"><label>Description</label><textarea className="ev-input" rows={3} value={form.description} onChange={(evt) => setField("description", evt.target.value)} /></div>
                            <div className="ev-field"><label>Limite de places</label><input type="number" className="ev-input" value={form.maxPlaces} onChange={(evt) => setField("maxPlaces", evt.target.value)} /></div>
                            <div className="ev-field"><label>Devise</label><input className="ev-input" value={form.currency} onChange={(evt) => setField("currency", evt.target.value)} /></div>
                            <div className="ev-field"><label>Délai d'inscription (heures)</label><input type="number" className="ev-input" value={form.registrationDeadlineHours} onChange={(evt) => setField("registrationDeadlineHours", evt.target.value)} /></div>
                            <div className="ev-field"><label>Mode périodique</label><input className="ev-input" value={form.isPeriodic ? "Activé" : "Désactivé"} disabled /></div>
                            <label className="ev-import-eval-card" style={{ justifyContent: "flex-start" }}><span className="ev-toggle"><input type="checkbox" checked={form.allowRegistration} onChange={(evt) => setField("allowRegistration", evt.target.checked)} /><span className="ev-slider" /></span><span>Autoriser les inscriptions</span></label>
                            <label className="ev-import-eval-card" style={{ justifyContent: "flex-start" }}><span className="ev-toggle"><input type="checkbox" checked={form.isPeriodic} onChange={(evt) => setField("isPeriodic", evt.target.checked)} /><span className="ev-slider" /></span><span>Événement périodique</span></label>
                        </div>
                    )}
                    {tab === "SESSIONS" && <div className="ev-box"><p className="ev-section-title">Nombre de séances</p><input type="number" min={0} max={30} className="ev-input" style={{ width: 100, marginBottom: 16 }} value={seancesCount} onChange={(evt) => handleSessionsCountChange(evt.target.value)} /><div className="ev-table-wrap"><table className="ev-table ev-table--sm"><thead><tr><th>#</th><th>Nom</th><th>Durée (h)</th><th>Début</th><th>Fin</th></tr></thead><tbody>{seances.map((session, index) => <tr key={session.id || index}><td>{session.sorder}</td><td><input className="ev-input ev-input--sm" value={session.name} onChange={(evt) => setSeances((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, name: evt.target.value } : item))} /></td><td><input type="number" step={0.5} className="ev-input ev-input--sm" value={session.dureeHours} onChange={(evt) => setSeances((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, dureeHours: evt.target.value } : item))} /></td><td><input type="datetime-local" className="ev-input ev-input--sm" value={session.startat} onChange={(evt) => setSeances((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, startat: evt.target.value } : item))} /></td><td><input type="datetime-local" className="ev-input ev-input--sm" value={session.endat} onChange={(evt) => setSeances((current) => current.map((item, itemIndex) => itemIndex === index ? { ...item, endat: evt.target.value } : item))} /></td></tr>)}</tbody></table></div></div>}
                    {tab === "CONSULTANTS" && <div className="ev-box"><div className="ev-search-wrap" style={{ marginBottom: 16 }}><Search size={14} className="ev-search-icon" /><input className="ev-search" placeholder="Rechercher consultant..." value={consultSearch} onChange={(evt) => setConsultSearch(evt.target.value)} /></div><div className="ev-grid ev-grid--cols-2">{filteredConsultants.map((item) => <div key={item.id} className="ev-import-eval-card"><label className="ev-toggle"><input type="checkbox" checked={selectedConsultantIds.has(item.id)} onChange={() => toggleConsultant(item.id)} /><span className="ev-slider" /></label><div className="ev-import-eval-info"><div style={{ fontWeight: 600 }}>{item.nomContact}</div><div className="ev-hint" style={{ fontSize: 9 }}>{item.specialite || item.emailContact || "Consultant"}</div></div></div>)}</div></div>}
                    {tab === "EVALUATIONS" && (
                        <div className="ev-box">
                            <div className="ev-grid ev-grid--cols-3" style={{ marginBottom: 16 }}>
                                <div className="ev-field">
                                    <label>Délai à chaud (J+)</label>
                                    <input type="number" className="ev-input" value={numberOfDaysHot} onChange={(evt) => setNumberOfDaysHot(Math.max(1, Number(evt.target.value) || 1))} />
                                </div>
                                <div className="ev-field">
                                    <label>Délai à froid (J+)</label>
                                    <input type="number" className="ev-input" value={numberOfDaysCold} onChange={(evt) => setNumberOfDaysCold(Math.max(1, Number(evt.target.value) || 1))} />
                                </div>
                                {/*<div className="ev-field">*/}
                                {/*    <label>Recherche évaluation</label>*/}
                                {/*    <input className="ev-input" value={evaluationSearch} onChange={(evt) => setEvaluationSearch(evt.target.value)} />*/}
                                {/*</div>*/}
                            </div>
                            
                            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                                <div>
                                    <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#204385", marginBottom: 12, paddingBottom: 4, borderBottom: "1px solid #e5e7eb" }}>
                                        Évaluations à chaud
                                    </h4>
                                    {hotEvaluations.length === 0 ? (
                                        <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", margin: "4px 0" }}>Aucune évaluation à chaud</p>
                                    ) : (
                                        <div className="ev-grid ev-grid--cols-2">
                                            {hotEvaluations.map((item) => (
                                                <div key={item.id} className="ev-import-eval-card">
                                                    <label className="ev-toggle">
                                                        <input type="checkbox" checked={selectedEvaluationIds.has(item.id)} onChange={() => toggleEvaluation(item.id)} />
                                                        <span className="ev-slider" />
                                                    </label>
                                                    <div className="ev-import-eval-info">
                                                        <span style={{ fontWeight: 550 }}>{(item.name || item.nom) }</span>
                                                        <span className="ev-badge ev-badge--soft-blue" style={{ fontSize: 9 }}>À chaud</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <h4 style={{ fontSize: "14px", fontWeight: "600", color: "#204385", marginBottom: 12, paddingBottom: 4, borderBottom: "1px solid #e5e7eb" }}>
                                        Évaluations à froid
                                    </h4>
                                    {coldEvaluations.length === 0 ? (
                                        <p style={{ fontSize: "12px", color: "#9ca3af", fontStyle: "italic", margin: "4px 0" }}>Aucune évaluation à froid</p>
                                    ) : (
                                        <div className="ev-grid ev-grid--cols-2">
                                            {coldEvaluations.map((item) => (
                                                <div key={item.id} className="ev-import-eval-card">
                                                    <label className="ev-toggle">
                                                        <input type="checkbox" checked={selectedEvaluationIds.has(item.id)} onChange={() => toggleEvaluation(item.id)} />
                                                        <span className="ev-slider" />
                                                    </label>
                                                    <div className="ev-import-eval-info">
                                                        <span style={{ fontWeight: 550 }}>{(item.name || item.nom) }</span>
                                                        <span className="ev-badge ev-badge--soft-blue" style={{ fontSize: 9 }}>À froid</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {tab === "BUDGET" && <div className="ev-box"><div className="ev-grid"><div className="ev-field"><label>Tarif formation</label><input type="number" className="ev-input" value={budget.tarifFormation} onChange={(evt) => setBudget((current) => ({ ...current, tarifFormation: evt.target.value }))} /></div><div className="ev-field"><label>Hébergement</label><input type="number" className="ev-input" value={budget.hebergement} onChange={(evt) => setBudget((current) => ({ ...current, hebergement: evt.target.value }))} /></div><div className="ev-field"><label>Transport</label><input type="number" className="ev-input" value={budget.transport} onChange={(evt) => setBudget((current) => ({ ...current, transport: evt.target.value }))} /></div><div className="ev-field"><label>Salle conférence</label><input type="number" className="ev-input" value={budget.salleConference} onChange={(evt) => setBudget((current) => ({ ...current, salleConference: evt.target.value }))} /></div><div className="ev-field"><label>Repas</label><input type="number" className="ev-input" value={budget.repas} onChange={(evt) => setBudget((current) => ({ ...current, repas: evt.target.value }))} /></div><div className="ev-field"><label>Autres charges</label><input type="number" className="ev-input" value={budget.autres} onChange={(evt) => setBudget((current) => ({ ...current, autres: evt.target.value }))} /></div><div className="ev-field ev-span-2"><label>Détail autres charges</label><textarea className="ev-input" rows={3} value={budget.autreDescription} onChange={(evt) => setBudget((current) => ({ ...current, autreDescription: evt.target.value }))} /></div></div><div style={{ marginTop: 24, padding: 16, background: "#f8f9fa", borderRadius: 8 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span>Total saisi :</span><b style={{ color: "#204385" }}>{totalCost.toFixed(2)} {form.currency}</b></div><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}><span>Budget campagne :</span><b style={{ color: "#204385" }}>{campaignBudget?.montant != null ? `${toFloat(campaignBudget.montant).toFixed(2)} MAD` : "—"}</b></div><div style={{ display: "flex", justifyContent: "space-between" }}><span>Restant campagne :</span><b style={{ color: remainingBudget < 0 ? "#ee645f" : "#059669" }}>{campaignBudget ? `${remainingBudget.toFixed(2)} MAD` : "—"}</b></div></div></div>}
                </div>
                <div className="ev-modal-footer">
                    <button type="button" className="ev-btn ev-btn-light" onClick={onClose}>Annuler</button>
                    <button type="button" className="ev-btn ev-btn-primary" onClick={handleSubmit} disabled={loading}>{loading ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer"}</button>
                </div>
            </div>
        </div>
    );
}
